import React, { useLayoutEffect, useContext } from "react";
import { useSelector } from "react-redux";
import DateLib from "../../../inc/DateLib";
import ApiNode from "../../../app/api/admin/node/ApiNode";
import Page from "../../modules/page";
import { ContextPage } from "../../modules/page/pageContext";
import { ContextContent } from "../../modules/content/contentContext";
import List from "../../modules/list";
import useListFilters from "../../modules/list/useListFilters";
import ListHeaderItem from "../../modules/list/__header-item";
import ListBodyItem from "../../modules/list/__body-item";
import { ContextList } from "../../modules/list/listContext";
import CommentCell from "../../modules/comment-cell";
import ButtonLinkShow from "../../components/button-link/__show";
import ButtonLinkAdd from "../../components/button-link/__add";
import InputNumber from "../../components/inputs/number/number";
import InputText from "../../components/inputs/text";
import Select from "../../components/inputs/select";
import "./styles.css";

// Список нод: сортировка и фильтры по NodeFilter (id/name/proxy_group_id).
export default function NodesIndex({ ...other }) {
    return <Page {...other}>
        <NodesIndexContent/>
    </Page>;
}

// Статус ноды (App\Console\Commands\ProcessNewNodesCommand, NodeProvisionScript,
// CheckCredsScript): new → provisioning → (invalid_credentials | provisioned | error).
const STATUS_BADGES = {
    'provisioned': 'badge_online',
    'provisioning': 'badge_warn',
    'new': 'badge_warn',
    'invalid_credentials': 'badge_offline',
    'error': 'badge_offline',
};

// Заметка ноды: PATCH nodes/{id} с ОДНИМ полем comment — строка списка правит только заметку.
// (Раньше частичный запрос был обязателен: NodesController::update сбрасывал статус ноды в `new`
// по одному присутствию ssh-поля и отправлял живую ноду на повторный провижн. Теперь правка полей
// провижн не запускает — он привязан к созданию ноды и кнопке, см. NodesController::update.)
function NodeCommentCell({ node }) {
    const contextList = useContext(ContextList);

    return <CommentCell
        value={node.comment}
        canEdit={true}
        onSave={async (value) => {
            const data = await ApiNode.Update(node.id, { comment: value }).getResponse();
            if (data) contextList.reload();
            return Boolean(data);
        }}
    />;
}

function NodesIndexContent() {
    const contextContent = useContext(ContextContent);
    const contextPage = useContext(ContextPage);
    const nodeStatuses = useSelector((state) => state.option.nodeStatuses);
    const proxyGroups = useSelector((state) => state.option.proxyGroups);

    const filter = useListFilters(['id', 'name', 'proxy_group_id']);

    useLayoutEffect(() => {
        contextContent.setTitle('Ноды');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const header = [
        <ListHeaderItem key="id" name="id">ID</ListHeaderItem>,
        <ListHeaderItem key="name" name="name">Имя</ListHeaderItem>,
        <ListHeaderItem key="comment" name="comment" isSort={false}>Заметка</ListHeaderItem>,
        <ListHeaderItem key="wan_ip" name="wan_ip">WAN IP</ListHeaderItem>,
        <ListHeaderItem key="architecture" name="architecture">Архитектура</ListHeaderItem>,
        // Сортировки нет: os_version не в whitelist'е NodeFilter::ORDER_COLUMNS — клик по
        // заголовку бэкенд молча проигнорировал бы.
        <ListHeaderItem key="os_version" name="os_version" isSort={false}>ОС</ListHeaderItem>,
        <ListHeaderItem key="status" name="status">Статус</ListHeaderItem>,
        <ListHeaderItem key="devices_count" name="devices_count">Устройств</ListHeaderItem>,
        <ListHeaderItem key="proxy_groups" name="proxy_groups" isSort={false}>Группы прокси</ListHeaderItem>,
        <ListHeaderItem key="create" name="create" isSort={false}>
            {contextContent.checkPermission('create')
                ? <ButtonLinkAdd className="button-link_white" title="Добавить" href={contextPage.routes.create.url()}/> : null}
        </ListHeaderItem>,
    ];

    const filters = [
        <InputNumber className="input_wf" value={filter.values.id} onChange={(e) => filter.setValue('id', e.target.value)}>id</InputNumber>,
        <InputText className="input_wf" value={filter.values.name} onChange={(e) => filter.setValue('name', e.target.value)}>Имя</InputText>,
        // «Без группы» нет: NodeFilter::proxyGroupId понимает только id группы.
        <Select className="input_wf" options={proxyGroups} value={filter.values.proxy_group_id} isNull={true}
            onChange={(e) => filter.setValue('proxy_group_id', e.target.value)}>Группа прокси</Select>,
    ];

    const renderRow = (row) => [
        <ListBodyItem key="id">{row.id}</ListBodyItem>,
        <ListBodyItem key="name">{row.name}</ListBodyItem>,
        <ListBodyItem key="comment"><NodeCommentCell node={row}/></ListBodyItem>,
        <ListBodyItem key="wan_ip">{row.wan_ip}</ListBodyItem>,
        <ListBodyItem key="architecture">{row.architecture ?? '—'}</ListBodyItem>,
        <ListBodyItem key="os_version">{row.os_version ?? '—'}</ListBodyItem>,
        <ListBodyItem key="status">
            <span className={`badge ${STATUS_BADGES[row.status] ?? ''}`}>
                {nodeStatuses[row.status] ?? row.status}
            </span>
        </ListBodyItem>,
        <ListBodyItem key="devices_count">{row.devices_count ?? 0}</ListBodyItem>,
        <ListBodyItem key="proxy_groups">
            {(row.proxy_group_ids?.length > 0)
                ? <span className="nodes__proxy-groups-cell">
                    {row.proxy_group_ids.map((id) => (proxyGroups[id] ?? `#${id}`)).join(', ')}
                </span>
                : '—'}
        </ListBodyItem>,
        <ListBodyItem key="show">
            {contextContent.checkPermission('show')
                ? <ButtonLinkShow className="button-link_blue" title="Просмотр" href={contextPage.routes.show.url({ id: row.id })}/> : null}
        </ListBodyItem>,
    ];

    return <List
        header={header}
        ApiModel={ApiNode}
        renderRow={renderRow}
        filters={filters}
        defaultSortKey="name"
        defaultSortIsAsc={true}
        searchParams={filter.applied}
        onApplyFilters={filter.apply}
        onResetFilters={filter.reset}
    />;
}
